import {Icon, isValidIcon, IconName, DuotoneIconName} from '@benchling/nucleus-ui';
import React, {HTMLAttributes} from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as logging from 'util/Logging';

import {IconAliasName, isIconAlias, resolveIconAlias} from './IconAliases';

export type BenchlingIconName = IconAliasName | IconName | DuotoneIconName;

export function isValidBenchlingIcon(name: string): name is BenchlingIconName {
  return isIconAlias(name) || isValidIcon(name);
}

export type BenchlingIconProps = HTMLAttributes<HTMLSpanElement> & {
  icon: BenchlingIconName;
  altText?: string;
  ifIconMissingReason?: string;
  dataTestAttrs?: DataTestAttrs;
};

export default function BenchlingIcon(props: BenchlingIconProps): JSX.Element {
  const {ifIconMissingReason, altText, ...childProps} = props;
  const dataTestAttrs = props.dataTestAttrs ?? {component: 'BenchlingIcon', key: props.icon};

  /**
   * Note: If you would like to add a new Nucleus UI icon, please reach out in #design-systems
   */
  const resolvedIcon = isIconAlias(props.icon) ? resolveIconAlias(props.icon) : props.icon;
  if (isValidIcon(resolvedIcon)) {
    return <Icon {...childProps} icon={resolvedIcon} altText={altText} dataTestAttrs={dataTestAttrs} />;
  } else {
    const reasonString = ifIconMissingReason ? ` ${ifIconMissingReason}.` : '';
    logging.warn(`Icon '${props.icon}' missing.${reasonString} Using notdef by default.`);
    return <Icon {...childProps} icon="notdef" />;
  }
}
